import { IconButton, Snackbar } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { useAppDispatch } from "app/hooks";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as InfoCircleIcon } from "components/Icons/info-circle.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import {
  Information as InformationType,
  removeInformation,
} from "reduxState/informations/informationsSlice";

export const informationHeight = 40;

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    accessToastWrapper: {
      display: "flex",
      alignItems: "center",
    },
    icon: {
      marginRight: theme.spacing(12),
    },
    warn: {
      color: theme.red600,
    },
    alertTitle: {
      fontWeight: "bold",
      marginRight: theme.spacing(8),
    },
    infoSnack: {
      "& > div": {
        backgroundColor: theme.white800,
        color: theme.white00,
      },
    },
    warnSnack: {
      "& > div": {
        backgroundColor: theme.red100,
        color: theme.red600,
      },
    },
  }),
);

const StyledSnackbar = withStyles({
  root: {
    top: "0",
    left: "0",
    width: "100%",
    fontSize: "16px",
    zIndex: 1200,
    maxHeight: `${informationHeight}px`,
    overflow: "hidden",
    transform: "unset",

    "& > div": {
      width: "100%",
      borderRadius: "0",
      padding: "0 24px",
      boxShadow: "none",
    },
  },
})(Snackbar);

export const InformationDetails = ({
  informationData,
  index,
}: {
  informationData: InformationType;
  index: number;
}) => {
  const classes = useStyles({ informationHeight });
  const { message } = useMessage();
  const dispatch = useAppDispatch();
  const handleClose = (information: InformationType) => {
    dispatch(removeInformation(information));
  };

  const getIcon = (type: InformationType["type"]) => {
    switch (type) {
      case "info":
        return <InfoCircleIcon className={classes.icon} />;
      case "warn":
        return (
          <AlertTriangleIcon className={`${classes.icon} ${classes.warn}`} />
        );
      default:
        return null;
    }
  };

  const getClassname = () => {
    switch (informationData.type) {
      case "info":
        return classes.infoSnack;
      case "warn":
        return classes.warnSnack;
      default:
        return "";
    }
  };

  return (
    <StyledSnackbar
      anchorOrigin={{ horizontal: "center", vertical: "top" }}
      message={
        <div className={classes.accessToastWrapper}>
          {getIcon(informationData.type)}

          <span className={classes.alertTitle}>
            {typeof informationData.title === "string"
              ? message({ id: informationData.title })
              : informationData.title}
          </span>
          {typeof informationData.description === "string"
            ? message({
                id: informationData.description,
              })
            : informationData.description}
        </div>
      }
      onClose={(_e, reason) => {
        if (reason === "clickaway") {
          return;
        }
        handleClose(informationData);
      }}
      open
      action={
        informationData.enableClose && (
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => handleClose(informationData)}
          >
            <XCloseIcon />
          </IconButton>
        )
      }
      autoHideDuration={informationData.autoHideDuration}
      sx={{
        top: index * informationHeight + "px !important",
      }}
      className={getClassname()}
    />
  );
};
